import { del, get, post, put, patch } from '@/lib/api';
import getPlatformApiBaseUrl from '@/lib/getPlatformApiBaseUrl';
import { parseJson } from '@shared/utils/parseJson.js';

export const getCompanies = () => {
  return get(`v1/companies`, {
    action: 'GET_COMPANIES',
    loader: 'getCompanies'
  });
};
export const postCompany = (data, callback) => {
  return post(`v1/companies`, {
    data,
    action: 'POST_COMPANY',
    callback
  });
};
export const putCompany = (data) => {
  return put(`v1/companies/${data.id}`, {
    data,
    action: 'PUT_COMPANY'
  });
};

export const getJobTypes = () => {
  return get(`v1/job-types`, {
    action: 'GET_JOB_TYPES',
    loader: 'getJobTypes'
  });
};
export const postJobType = (data) => {
  return post(`v1/job-types`, {
    data,
    action: 'POST_JOB_TYPE'
  });
};
export const putJobType = (data) => {
  return put(`v1/job-types/${data.id}`, {
    data,
    action: 'PUT_JOB_TYPE'
  });
};

export const getConnections = () => {
  return get(`v1/connections`, {
    action: 'GET_CONNECTIONS',
    loader: 'getConnections'
  });
};
export const postConnection = (data) => {
  return post(`v1/connections`, {
    data,
    action: 'POST_CONNECTION'
  });
};
export const putConnection = (data) => {
  return put(`v1/connections/${data.id}`, {
    data,
    action: 'PUT_CONNECTION'
  });
};

export const getUsers = () => {
  return get(`v1/users`, {
    action: 'GET_USERS',
    loader: 'getUsers'
  });
};

export const postUser = (data) => {
  return post(`v1/users`, {
    data,
    action: 'POST_USER'
  });
};
export const grantUserCirraCredits = ({ userId, companyId, amountUsd }, callback) => {
  return post(`v1/users/${userId}/cirra-credits`, {
    data: { amountUsd, companyId },
    action: 'GRANT_USER_CIRRA_CREDITS',
    callback
  });
};
export const grantCompanyCirraCredits = ({ companyId, amountUsd }, callback) => {
  return post(`v1/companies/${companyId}/cirra-credits`, {
    data: { amountUsd },
    action: 'GRANT_COMPANY_CIRRA_CREDITS',
    callback
  });
};
export const putUser = (data) => {
  return put(`v1/users/${data.id}`, {
    data,
    action: 'PUT_USER'
  });
};

export const getUser = ({ id }) => {
  return get(`v1/users/${id}`, {
    action: 'GET_USER'
  });
};

export const getUserPermissions = ({ id }) => {
  return get(`v1/users/${id}/permissions`, {
    action: 'GET_ACTIVE_USER_PERMISSIONS'
  });
};

export const postUserPermission = ({ userId, permission, companyId }, callback) => {
  return post(`v1/users/${userId}/permissions`, {
    data: { permission, companyId },
    action: 'POST_USER_PERMISSION',
    callback
  });
};

export const deleteUserPermission = ({ userId, permission }, callback) => {
  return del(`v1/users/${userId}/permissions/${permission}`, {
    action: 'DELETE_USER_PERMISSION',
    callback
  });
};

export const getCompanyConnections = (data) => {
  return get(`v1/company-connections`, {
    query: data,
    action: 'GET_COMPANY_CONNECTIONS',
    loader: 'getCompanyConnections'
  });
};

export const postCompanyConnection = ({ connectionId, callback }) => {
  return post(`v1/company-connections`, {
    action: 'GET_COMPANY_CONNECTIONS',
    data: {
      connectionId
    },
    callback
  });
};

export const getCompanyConnectionConfig = ({ connectionId }, callback) => {
  return get(`v1/company-connections/${connectionId}/config`, {
    action: 'GET_COMPANY_CONNECTION_CONFIG',
    callback
  });
};

export const getUserCompaniesByUserId = ({ userId, impersonate }, callback) => {
  return get(`v1/users/${userId}/companies`, {
    query: { impersonate },
    action: 'GET_USER_COMPANIES_BY_USER',
    callback,
    meta: { userId }
  });
};

export const postUserCompany = ({ userId, companyId }, callback) => {
  return post(`v1/users/${userId}/company/`, {
    data: { companyId },
    callback
  });
};

export const postSyncUsers = (callback) => {
  return post(`v1/users/sync`, {
    callback
  });
};

export const postSyncMetadata = ({ userId }, callback) => {
  return post(`v1/users/sync-metadata`, {
    query: { userId },
    callback
  });
};

export const deleteUserCompany = ({ userId, companyId }, callback) => {
  return del(`v1/users/${userId}/companies/${companyId}`, {
    data: { companyId },
    callback
  });
};

export const getProductFeatures = (data, callback) => {
  return get(`v1/billing/product-features`, {
    callback,
    action: 'GET_PRODUCT_FEATURES',
    loader: 'getProductFeatures'
  });
};

export const putProductFeaturesBatch = (data, callback) => {
  return put(`v1/billing/product-features/${data.stripeProductId}/batch`, {
    data: {
      batch: data.batch
    },
    callback,
    action: 'GET_PRODUCT_FEATURES'
  });
};

export const getCompanyUsage = () => {
  return get(`v1/billing/usage`, {
    action: 'GET_COMPANY_USAGE'
  });
};

export const getCompanySubscription = ({ companyId }) => {
  return get(`v1/stripe/company/${companyId}`, {
    action: 'GET_COMPANY_SUBSCRIPTION'
  });
};

export const createCustomerPortal = ({ companyId, returnUrl }) => {
  return post('v1/stripe/portal', {
    data: {
      companyId,
      returnUrl: returnUrl || window.location.href
    }
  });
};

export const upgradePlan = ({ subscriptionId, productId, featureId, featureOptionId, price, interval }) => {
  return post(`v1/stripe/upgrade-plan`, {
    data: {
      subscriptionId,
      productId,
      featureId,
      featureOptionId,
      price,
      interval
    }
  });
};

export const createCheckoutSession = ({ productId, email, yearly }) => {
  return post(`v1/stripe/checkout`, {
    data: {
      productId,
      email,
      returnUrl: window.location.href,
      yearly
    }
  });
};

export const upgradeToEnterprise = ({ companyId, ownerEmail, logRetention, tasks, jobsScreen, planPrice, runnerSize, apps, workflows, interval, isLegacy }) => {
  return post(`v1/stripe/enterprise`, {
    data: {
      companyId,
      ownerEmail,
      logRetention,
      tasks,
      jobsScreen,
      planPrice,
      runnerSize,
      apps,
      workflows,
      interval,
      isLegacy
    }
  });
};

export const getRoles = ({ admin, companyId }) => {
  return get('v1/roles', {
    query: !admin && companyId ? { companyId } : {},
    action: 'GET_ROLES'
  });
};

export const createRole = ({ name, description }) => {
  return post('v1/roles', {
    data: { name, description },
    action: 'CREATE_ROLE'
  });
};

export const putRole = ({ id, name, description }) => {
  return put(`v1/roles/${id}`, {
    data: { name, description },
    action: 'PUT_ROLE'
  });
};

export const deleteRole = ({ id }) => {
  return del(`v1/roles/${id}`, {
    action: 'DELETE_ROLE',
    meta: { id }
  });
};

export const getPermissions = ({ onlyNonAdmin = false } = {}) => {
  return get('v1/permissions', {
    action: 'GET_PERMISSIONS',
    query: { onlyNonAdmin }
  });
};

export const postPermission = ({ key, name }) => {
  return post('v1/permissions', {
    data: { key, name },
    action: 'POST_PERMISSION'
  });
};

export const putPermission = ({ id, key, name }) => {
  return put(`v1/permissions/${id}`, {
    data: { key, name },
    action: 'PUT_PERMISSION'
  });
};

export const getAllRolesPermissions = () => {
  return get(`v1/role-permissions`, {
    action: 'GET_ALL_ROLES_PERMISSIONS'
  });
};

export const createRolePermission = ({ roleId, permissionId }) => {
  return post(`v1/role-permissions`, {
    data: { roleId, permissionId },
    action: 'CREATE_ROLE_PERMISSION'
  });
};

export const deleteRolePermission = ({ roleId, permissionId }) => {
  return del(`v1/role-permissions`, {
    data: { roleId, permissionId },
    meta: { roleId, permissionId },
    action: 'DELETE_ROLE_PERMISSION'
  });
};

export const getInvites = () => {
  return get(`v1/invite`, {
    action: 'GET_INVITES',
    loader: 'getInvites'
  });
};

export const postNewInvite = ({ email, companyName, interval, plan, tasks, price, bundleId, companyId, isSignUp, allowedRecipients, expiry }, callback) => {
  return post(`v1/invite`, {
    data: { email, companyName, interval, plan, tasks, price, bundleId, companyId, isSignUp, allowedRecipients, expiry },
    action: 'POST_NEW_INVITE',
    loader: 'postNewInvite',
    callback
  });
};

export const putInvite = ({ id, interval, plan, tasks, price }) => {
  return put(`v1/invite/${id}`, {
    data: { interval, plan, tasks, price },
    action: 'PUT_INVITE'
  });
};

export const deleteInvite = ({ id }) => {
  return del(`v1/invite/${id}`, {
    action: 'DELETE_INVITE',
    meta: { inviteId: id }
  });
};

export const processInvite = ({ inviteId }, callback) => {
  return post(`v1/invite/${inviteId}/process`, {
    action: 'PROCESS_INVITE',
    loader: 'processInvite',
    callback
  });
};

export const postBundle = (data) => {
  return post(`v1/bundles`, {
    data,
    action: 'POST_BUNDLE'
  });
};

export const putBundle = (data) => {
  return put(`v1/bundles/${data.id}`, {
    data,
    action: 'PUT_BUNDLE',
    meta: { id: data.id, name: data.name, recipes: data.recipes }
  });
};

export const deleteBundle = ({ id }) => {
  return del(`v1/bundles/${id}`, {
    action: 'DELETE_BUNDLE',
    meta: { bundleId: id }
  });
};

export const processBundle = ({ inviteId, companyId, bundleId, tempCompanyId }, callback) => {
  return post(`v1/bundles/process`, {
    data: { companyId, tempCompanyId },
    query: { bundleId, inviteId },
    action: 'PROCESS_BUNDLE',
    loader: 'processBundle',
    callback
  });
};

export const getCompanyMembers = () => {
  return get(`v1/members`, {
    action: 'GET_COMPANY_MEMBERS'
  });
};

export const inviteMember = ({ emails }) => {
  return post(`v1/members`, {
    data: { emails },
    action: 'INVITE_MEMBER'
  });
};

export const updateMember = ({ userId, roleId }) => {
  return put(`v1/members/${userId}`, {
    data: { roleId },
    action: 'UPDATE_MEMBER'
  });
};

export const deleteMember = ({ userId }) => {
  return del(`v1/members/${userId}`, {
    action: 'DELETE_MEMBER',
    meta: { userId }
  });
};

export const convertCompany = ({ name, convert, emails, callback }) => {
  return post(`v1/companies/convert`, {
    data: { name, convert, emails },
    action: 'CONVERT_COMPANY',
    callback
  });
};

export const deleteCompany = () => {
  return del(`v1/companies`, {
    action: 'DELETE_COMPANY'
  });
};

export const getDashboardState = ({ from, to, allTime }) => {
  return get(`v1/dashboard/state`, {
    query: { from, to, allTime },
    action: 'GET_DASHBOARD_STATE'
  });
};

export const checkCompanyName = (data) => {
  return get(`v1/companies/check-name`, {
    query: data,
    action: 'CHECK_COMPANY_NAME',
    loader: 'checkCompanyName'
  });
};

export const getIncidents = () => {
  return get(`v1/status/incidents`, {
    action: 'GET_INCIDENTS'
  });
};

export const reportIncident = ({ summary, description, critical }, callback) => {
  return post(`v1/incident-reports`, {
    data: { summary, description, critical },
    action: 'REPORT_INCIDENT',
    loader: 'reportIncident',
    callback
  });
};

export const getPromptEvals = ({ from, to } = {}) => {
  return get(`v1/ai/prompt-evals`, {
    query: { from, to },
    action: 'GET_PROMPT_EVALS',
    loader: 'getPromptEvals'
  });
};

export const getPromptEvalsByName = ({ promptName, from, to }) => {
  return get(`v1/ai/prompt-evals-by-name`, {
    query: { promptName: encodeURIComponent(promptName), from, to },
    action: 'GET_PROMPT_EVALS_BY_NAME',
    loader: 'getPromptEvalsByName'
  });
};

export const getPromptEvalById = ({ id }) => {
  return get(`v1/ai/prompt-eval-by-id`, {
    query: { id },
    action: 'GET_PROMPT_EVAL_BY_ID',
    loader: 'getPromptEvalById'
  });
};

export const postCompanyEnvironment = ({ name }) => {
  return post(`v1/companies/environments`, {
    data: { name },
    action: 'POST_COMPANY_ENVIRONMENT',
    loader: 'postCompanyEnvironment'
  });
};

export const putCompanyEnvironment = ({ environmentId, name }) => {
  return put(`v1/companies/environments/${environmentId}`, {
    data: { name },
    action: 'PUT_COMPANY_ENVIRONMENT',
    loader: 'putCompanyEnvironment'
  });
};

export const deleteCompanyEnvironment = ({ environmentId }) => {
  return del(`v1/companies/environments/${environmentId}`, {
    action: 'DELETE_COMPANY_ENVIRONMENT',
    loader: 'deleteCompanyEnvironment'
  });
};

export const getUserTracking = () => {
  return get(`v1/users/tracking`, {
    action: 'GET_USER_TRACKING',
    loader: 'getUserTracking'
  });
};

export const putUserTracking = (data) => {
  return put(`v1/users/tracking/${encodeURIComponent(data?.email)}`, {
    data,
    action: 'PUT_USER_TRACKING',
    loader: 'putUserTracking'
  });
};

export const updateUserTracking = () => {
  return put(`v1/users/tracking`, {
    action: 'UPDATE_USER_TRACKING',
    loader: 'updateUserTracking'
  });
};

export const postUserTrackingComment = ({ email, comment, mentions }, callback) => {
  return post(`v1/users/tracking/${encodeURIComponent(email)}/comments`, {
    data: { comment, mentions },
    action: 'POST_USER_TRACKING_COMMENT',
    loader: 'postUserTrackingComment',
    meta: { email },
    callback
  });
};

export const putUserTrackingComment = ({ email, commentId, comment, mentions }, callback) => {
  return put(`v1/users/tracking/${encodeURIComponent(email)}/comments/${commentId}`, {
    data: { comment, mentions },
    action: 'PUT_USER_TRACKING_COMMENT',
    meta: { email, commentId },
    callback
  });
};

export const deleteUserTrackingComment = ({ email, commentId }, callback) => {
  return del(`v1/users/tracking/${encodeURIComponent(email)}/comments/${commentId}`, {
    action: 'DELETE_USER_TRACKING_COMMENT',
    meta: { email, commentId },
    callback
  });
};

export const getApiKeys = () => {
  return get(`v1/api-keys`, {
    action: 'GET_API_KEYS',
    loader: 'getApiKeys'
  });
};

export const createApiKey = (data, callback) => {
  return post(`v1/api-keys`, {
    data,
    action: 'CREATE_API_KEY',
    loader: 'createApiKey',
    callback
  });
};

export const revokeApiKey = ({ keyId }, callback) => {
  return put(`v1/api-keys/${keyId}/revoke`, {
    action: 'REVOKE_API_KEY',
    loader: 'revokeApiKey',
    meta: { keyId },
    callback
  });
};

export const deleteApiKey = ({ keyId }, callback) => {
  return del(`v1/api-keys/${keyId}`, {
    action: 'DELETE_API_KEY',
    loader: 'deleteApiKey',
    meta: { keyId },
    callback
  });
};

export const getLinearUsers = () => {
  return get(`v1/projects/users`, {
    action: 'GET_LINEAR_USERS',
    loader: 'getLinearUsers'
  });
};

export const getHubspotSupportTeamMemberOptions = ({ refresh = false } = {}) => {
  return get(`v1/projects/hubspot-support-team-member-options`, {
    query: refresh ? { refresh: 'true' } : {},
    action: 'GET_HUBSPOT_SUPPORT_TEAM_MEMBER_OPTIONS',
    loader: 'getHubspotSupportTeamMemberOptions'
  });
};

export const getAdminUsers = () => {
  return get(`v1/users/admin-users`, {
    action: 'GET_ADMIN_USERS',
    loader: 'getAdminUsers'
  });
};

export const syncLinearUsers = (callback) => {
  return post(`v1/projects/syncUsers`, {
    action: 'SYNC_LINEAR_USERS',
    loader: 'syncLinearUsers',
    callback
  });
};

export const getFtpUsers = () => {
  return get('v1/ftp-users', {
    action: 'GET_FTP_USERS',
    loader: 'getFtpUsers'
  });
};

export const getFtpUserById = ({ id }) => {
  return get(`v1/ftp-users/${id}`, {
    action: 'GET_FTP_USER'
  });
};

export const createFtpUser = (data, callback) => {
  return post('v1/ftp-users', {
    data,
    action: 'CREATE_FTP_USER',
    callback
  });
};

export const updateFtpUser = ({ id, ...data }, callback) => {
  return put(`v1/ftp-users/${id}`, {
    data,
    action: 'UPDATE_FTP_USER',
    callback
  });
};

export const deleteFtpUser = ({ id }, callback) => {
  return del(`v1/ftp-users/${id}`, {
    action: 'DELETE_FTP_USER',
    meta: { id },
    callback
  });
};

export const getPublicSlackChannelUsers = (callback) => {
  // workflow id: IpN4aHYvb5MD
  const local = window.location.hostname.includes('localhost') || window.location.hostname.includes('127.0.0.1');
  const webhookBaseUrl = local ? getPlatformApiBaseUrl() : 'https://webhook.mindcloud.co/';
  return post(`${webhookBaseUrl}v1/webhook/611078f4fde264cc1b853042490bf533fc4ebd18be0905d78a6065daecbe9aabd798c02eec07670ba87dcf242eec1732`, {
    action: 'GET_PUBLIC_SLACK_CHANNEL_USERS',
    loader: 'getPublicSlackChannelUsers',
    callback,
    onResponse: (response) => {
      const data = response?.data;
      if (typeof data === 'string') {
        const parsed = parseJson(data, { fallback: true });
        if (Array.isArray(parsed)) {
          response.data = { data: parsed };
        } else if (parsed && typeof parsed === 'object' && parsed.data !== undefined) {
          response.data = parsed;
        } else {
          response.data = { data: parsed };
        }
      }
      return response;
    }
  });
};

export const updateLinearUser = ({ projectsUserId, userId }, callback) => {
  return patch(`v1/projects/updateUser`, {
    data: { userId, projectsUserId },
    action: 'UPDATE_SERVICE_USER',
    callback
  });
};

export const updateServiceUserRole = ({ serviceUserId, role }, callback) => {
  return patch(`v1/projects/updateUserRole`, {
    data: { role, userId: serviceUserId },
    action: 'UPDATE_SERVICE_USER',
    callback
  });
};

export const updateServiceUserPm = ({ serviceUserId, pmId }, callback) => {
  return patch(`v1/projects/updateUserPm`, {
    data: { pmId, userId: serviceUserId },
    action: 'UPDATE_SERVICE_USER',
    callback
  });
};

export const updateServiceUserSlackUser = ({ serviceUserId, slackUserId }, callback) => {
  return patch(`v1/projects/updateUserSlackUser`, {
    data: { userId: serviceUserId, slackUserId },
    action: 'UPDATE_SERVICE_USER',
    callback
  });
};

export const updateServiceUserHubspotSupportTeamMember = ({ serviceUserId, hubspotSupportTeamMemberKey }, callback) => {
  return patch(`v1/projects/service-users/${serviceUserId}/hubspot-support-team-member`, {
    data: { hubspotSupportTeamMemberKey },
    action: 'UPDATE_SERVICE_USER',
    callback
  });
};

export const updateServiceUserPhoto = ({ serviceUserId, photoUrl }, callback) => {
  return patch(`v1/projects/updateUserPhoto`, {
    data: { userId: serviceUserId, photoUrl },
    action: 'UPDATE_SERVICE_USER',
    callback
  });
};
